import { ImageView, View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import { assertNever } from 'src/replicant/utils';
import StateObserver from 'src/StateObserver';
import PopupMatch2Base, { PopupMatch2BaseInitOptions } from './PopupMatch2Base';
import { Match2FrenzyEventConfig } from 'src/replicant/match2/match2.ruleset';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import Match2FrenzyBar from './Match2FrenzyBar';
import { getMatch2FrenzyEventConfig } from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import { AssetGroup } from '@play-co/timestep-core';
import { playMatch2 } from '../match2.play';

interface PopupMatch2FrenzyIntroInitOptions extends PopupMatch2BaseInitOptions {
  skipPlay?: boolean;
}

export default class PopupMatch2FrenzyIntro extends PopupMatch2Base {
  private config: Match2FrenzyEventConfig;
  private skipPlay = false;
  private frenzyBar: Match2FrenzyBar;

  constructor(opts: { superview: View; close: (success: boolean) => void }) {
    super({
      superview: opts.superview,
      width: 569,
      height: 805,
      panelImageOpts: {
        image: 'assets/ui/match2/common/panel_intro_frenzy.png',
      },
      offsetY: 45,
      close: () => opts.close(false),
      title: { type: 'image' },
      cta: {
        localeText: () => i18n('match2.ui.play').toLocaleUpperCase(),
        positioning: 'edge',
        offsetY: -93,
        onClick: async () => {
          opts.close(true);
          if (!this.skipPlay) playMatch2({ origin: undefined });
        },
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.FRENZY_INTRO_POPUP,
      },
    });

    const { user } = StateObserver.getState();
    const now = StateObserver.now();
    const config = getMatch2FrenzyEventConfig(user, now)!;
    this.config = config;

    new ImageView({
      superview: this.panelView,
      ...this.getPopupBackground(),
      canHandleEvents: false,
      x: this.panel.style.width * 0.5,
      y: this.panel.style.height * 0.5 - 107,
      centerOnOrigin: true,
      centerAnchor: true,
    });

    new LangBitmapFontTextView({
      superview: this.panel,
      localeText: () => i18n(`match2.frenzy.${config.theme}`),
      font: 'Body',
      color: '#BF8147',
      width: this.panel.style.width * 0.9,
      size: 22.5,
      x: this.panel.style.width * 0.5,
      y: 600,
      centerOnOrigin: true,
      centerAnchor: true,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
    });

    this.frenzyBar = new Match2FrenzyBar({
      superview: this.panel,
      x: this.panel.style.width * 0.5 - 380 * 0.5,
      y: 495,
      syncState: true,
      canHandleEvents: false,
    });
  }

  protected getAssetsGroup() {
    // Load all assets if theme is somehow not set.
    const theme = this.config.theme ?? '';
    return new AssetGroup(
      AssetGroup.constructURLs([
        'assets/ui/match2/frenzy/common',
        `assets/ui/match2/frenzy/${theme}`,
      ]),
    );
  }

  public init({
    skipPlay = false,
    useClickOutside,
  }: PopupMatch2FrenzyIntroInitOptions) {
    this.setTitleImage({
      ...this.getPopupTitle(),
      y: 12,
      centerOnOrigin: true,
      centerAnchor: true,
    });
    super.init({ useClickOutside });
    this.frenzyBar.autoUpdate();
    this.skipPlay = skipPlay;
    this.buttonCta!.localeText = () =>
      this.skipPlay
        ? i18n('basic.continue').toLocaleUpperCase()
        : i18n('match2.ui.play').toLocaleUpperCase();
  }

  public fadeOut() {
    this.frenzyBar.stopAutoUpdate();
    return super.fadeOut();
  }

  /**
   * Get popup background
   */
  private getPopupBackground() {
    return {
      image: `assets/ui/match2/frenzy/${this.config.theme}/${this.config.theme}_festival.png`,
      width: 500,
      height: 500,
    };
  }

  /**
   * Get popup title
   */
  private getPopupTitle() {
    const locale = StateObserver.getLocale() === 'en' ? 'en' : 'jp';

    switch (this.config.theme) {
      case 'leaf':
        return {
          image: `assets/ui/match2/frenzy/leaf/leaf_title_${locale}.png`,
          width: locale === 'en' ? 268 : 400,
          height: locale === 'en' ? 165 : 107,
        };
      case 'firecracker':
        return {
          image: `assets/ui/match2/frenzy/firecracker/firecracker_title_${locale}.png`,
          width: locale === 'en' ? 446 : 337,
          height: locale === 'en' ? 153 : 106,
        };
      default:
        assertNever(this.config.theme);
    }
  }
}
